import React, { useState, useEffect } from 'react';
import { ElectrodeType, InspectionBreakdown } from '@twinsketch/topika-model';
import 'src/styles/workOrder/mixing-table.css';
import { FiEdit2, FiCheck, FiX } from 'react-icons/fi';

interface InspectionTableProps {
  inspection: InspectionBreakdown | null | undefined;
  onUpdate: (data: InspectionBreakdown) => Promise<void>;
  readonly?: boolean;
  electrodeType?: ElectrodeType; // Add this prop
}

const DEFAULT_BINDER_VISCOSITY_CONDITION = '@25±3℃, #63 spindle, 60rpm';
const DEFAULT_SLURRY_VISCOSITY_CONDITION = '@25±3℃, #64 spindle, 60rpm';

const DEFAULT_VALUES = {
  binderSolidContent: 7.0,
  binderSolidContentTolerance: 1.0,
  binderViscosityValue: 2000,
  binderViscosityTolerance: 500,
  slurrySolidContent: 57.78,
  slurrySolidContentTolerance: 2.0,
  slurryViscosityMin: 5000,
  slurryViscosityMax: 6000,
};

const NUMERIC_FIELDS: (keyof InspectionBreakdown)[] = [
  'binderSolidContent',
  'binderSolidContentTolerance',
  'binderViscosityValue',
  'binderViscosityTolerance',
  'slurrySolidContent',
  'slurrySolidContentTolerance',
  'slurryViscosityMin',
  'slurryViscosityMax',
];

const InspectionTable: React.FC<InspectionTableProps> = ({
  inspection,
  onUpdate,
  readonly = false,
  electrodeType,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<InspectionBreakdown>(createInitialFormData(inspection));

  const binderLabel = electrodeType === ElectrodeType.ANODE ? 'CMC solution' : 'Binder Solution';

  useEffect(() => {
    if (inspection) {
      setFormData(createInitialFormData(inspection));
    }
  }, [inspection]);

  const handleChange = (field: keyof InspectionBreakdown, value: string) => {
    if (NUMERIC_FIELDS.includes(field)) {
      const numValue = value === '' ? undefined : Number(value);
      setFormData(prev => ({ ...prev, [field]: numValue }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleEditClick = () => {
    // Apply default values for any numeric field that is null, undefined, or 0
    const dataWithDefaults: InspectionBreakdown = {
      binderSolidContent: inspection?.binderSolidContent ?? DEFAULT_VALUES.binderSolidContent,
      binderSolidContentTolerance:
        inspection?.binderSolidContentTolerance ?? DEFAULT_VALUES.binderSolidContentTolerance,
      binderViscosityValue: inspection?.binderViscosityValue ?? DEFAULT_VALUES.binderViscosityValue,
      binderViscosityTolerance:
        inspection?.binderViscosityTolerance ?? DEFAULT_VALUES.binderViscosityTolerance,
      binderViscosityUnit: 'cps',
      binderViscosityCondition:
        inspection?.binderViscosityCondition || DEFAULT_BINDER_VISCOSITY_CONDITION,

      slurrySolidContent: inspection?.slurrySolidContent ?? DEFAULT_VALUES.slurrySolidContent,
      slurrySolidContentTolerance:
        inspection?.slurrySolidContentTolerance ?? DEFAULT_VALUES.slurrySolidContentTolerance,
      slurrySolidContentNote: inspection?.slurrySolidContentNote || '',
      slurryViscosityMin: inspection?.slurryViscosityMin ?? DEFAULT_VALUES.slurryViscosityMin,
      slurryViscosityMax: inspection?.slurryViscosityMax ?? DEFAULT_VALUES.slurryViscosityMax,
      slurryViscosityUnit: 'cps',
      slurryViscosityCondition:
        inspection?.slurryViscosityCondition || DEFAULT_SLURRY_VISCOSITY_CONDITION,
    };

    setFormData(dataWithDefaults);
    setIsEditing(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await onUpdate(formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save inspection info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData(createInitialFormData(inspection));
    setIsEditing(false);
  };

  const canEdit = !readonly && !isEditing;

  return (
    <>
      <div className="formulation-title-row">
        <span className="formulation-title">Insepction</span>

        {canEdit && (
          <div className="action-buttons">
            <button
              className="formulation-link"
              onClick={() => setIsEditing(true)}
              title="Edit"
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                padding: '4px',
              }}
            >
              <FiEdit2 size={16} color="#666" />
            </button>
          </div>
        )}

        {isEditing && (
          <div className="action-buttons" style={{ marginBottom: '8px' }}>
            <button
              className="formulation-link"
              onClick={handleSave}
              disabled={loading}
              title="Save"
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                padding: '4px',
                marginRight: '4px',
              }}
            >
              <FiCheck size={16} color="#28a745" />
            </button>
            <button
              className="formulation-link"
              onClick={handleCancel}
              disabled={loading}
              title="Cancel"
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                padding: '4px',
              }}
            >
              <FiX size={16} color="#dc3545" />
            </button>
          </div>
        )}
      </div>

      <table className="mixing-table">
        <tbody>
          {/* Binder/CMC Solution - Solid Content */}
          <tr>
            <td rowSpan={2} style={{ width: '20%' }}>
              {binderLabel}
            </td>
            <td style={{ width: '20%' }}>Solid content</td>
            <td style={{ width: '60%' }}>
              {isEditing ? (
                <SolidContentInput
                  value={formData.binderSolidContent}
                  tolerance={formData.binderSolidContentTolerance}
                  onValueChange={val => handleChange('binderSolidContent', val)}
                  onToleranceChange={val => handleChange('binderSolidContentTolerance', val)}
                  valuePlaceholder="7.00"
                  tolerancePlaceholder="1.00"
                />
              ) : (
                formatSolidContent(
                  inspection?.binderSolidContent,
                  inspection?.binderSolidContentTolerance
                )
              )}
            </td>
          </tr>

          {/* Binder/CMC Solution - Viscosity */}
          <tr>
            <td>Viscosity</td>
            <td>
              {isEditing ? (
                <ViscosityInput
                  value={formData.binderViscosityValue}
                  tolerance={formData.binderViscosityTolerance}
                  condition={formData.binderViscosityCondition}
                  onValueChange={val => handleChange('binderViscosityValue', val)}
                  onToleranceChange={val => handleChange('binderViscosityTolerance', val)}
                  onConditionChange={val => handleChange('binderViscosityCondition', val)}
                  valuePlaceholder="2000"
                  tolerancePlaceholder="500"
                  conditionPlaceholder="Condition: @25±3℃, #63 spindle, 60rpm"
                />
              ) : (
                formatBinderViscosity(
                  inspection?.binderViscosityValue,
                  inspection?.binderViscosityTolerance,
                  inspection?.binderViscosityUnit,
                  inspection?.binderViscosityCondition
                )
              )}
            </td>
          </tr>

          {/* Slurry - Solid Content */}
          <tr>
            <td rowSpan={2}>Slurry</td>
            <td>Solid content</td>
            <td>
              {isEditing ? (
                <SolidContentWithNoteInput
                  value={formData.slurrySolidContent}
                  tolerance={formData.slurrySolidContentTolerance}
                  note={formData.slurrySolidContentNote}
                  onValueChange={val => handleChange('slurrySolidContent', val)}
                  onToleranceChange={val => handleChange('slurrySolidContentTolerance', val)}
                  onNoteChange={val => handleChange('slurrySolidContentNote', val)}
                  valuePlaceholder="57.78"
                  tolerancePlaceholder="2.00"
                  notePlaceholder="Note (Reference)"
                />
              ) : (
                formatSolidContent(
                  inspection?.slurrySolidContent,
                  inspection?.slurrySolidContentTolerance,
                  inspection?.slurrySolidContentNote
                )
              )}
            </td>
          </tr>

          {/* Slurry - Viscosity */}
          <tr>
            <td>Viscosity</td>
            <td>
              {isEditing ? (
                <ViscosityRangeInput
                  min={formData.slurryViscosityMin}
                  max={formData.slurryViscosityMax}
                  condition={formData.slurryViscosityCondition}
                  onMinChange={val => handleChange('slurryViscosityMin', val)}
                  onMaxChange={val => handleChange('slurryViscosityMax', val)}
                  onConditionChange={val => handleChange('slurryViscosityCondition', val)}
                  minPlaceholder="Min (5000)"
                  maxPlaceholder="Max (6000)"
                  conditionPlaceholder="Condition: @25±3℃, #64 spindle, 60rpm"
                />
              ) : (
                formatSlurryViscosity(
                  inspection?.slurryViscosityMin,
                  inspection?.slurryViscosityMax,
                  inspection?.slurryViscosityUnit,
                  inspection?.slurryViscosityCondition
                )
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

// ========== Helper Functions ==========

function createInitialFormData(
  inspection: InspectionBreakdown | null | undefined
): InspectionBreakdown {
  return {
    binderSolidContent: inspection?.binderSolidContent,
    binderSolidContentTolerance: inspection?.binderSolidContentTolerance,
    binderViscosityValue: inspection?.binderViscosityValue,
    binderViscosityTolerance: inspection?.binderViscosityTolerance,
    binderViscosityUnit: 'cps',
    binderViscosityCondition:
      inspection?.binderViscosityCondition || DEFAULT_BINDER_VISCOSITY_CONDITION,

    slurrySolidContent: inspection?.slurrySolidContent,
    slurrySolidContentTolerance: inspection?.slurrySolidContentTolerance,
    slurrySolidContentNote: inspection?.slurrySolidContentNote || '',
    slurryViscosityMin: inspection?.slurryViscosityMin,
    slurryViscosityMax: inspection?.slurryViscosityMax,
    slurryViscosityUnit: 'cps',
    slurryViscosityCondition:
      inspection?.slurryViscosityCondition || DEFAULT_SLURRY_VISCOSITY_CONDITION,
  };
}

function formatSolidContent(value?: number, tolerance?: number, note?: string): string {
  if (value === null || value === undefined) return 'N/A';

  let result = `${value}%`;
  if (tolerance !== null && tolerance !== undefined) {
    result += ` ± ${tolerance}%`;
  }
  if (note) {
    result += ` ${note}`;
  }
  return result;
}

function formatBinderViscosity(
  value?: number,
  tolerance?: number,
  unit?: string,
  condition?: string
): string {
  if (value === null || value === undefined) return 'N/A';

  let result = `${value}`;
  if (tolerance !== null && tolerance !== undefined) {
    result += ` ± ${tolerance}`;
  }
  if (unit) {
    result += ` ${unit}`;
  }
  if (condition) {
    result += ` ${condition}`;
  }
  return result;
}

function formatSlurryViscosity(
  min?: number,
  max?: number,
  unit?: string,
  condition?: string
): string {
  if ((min === null || min === undefined) && (max === null || max === undefined)) {
    return 'N/A';
  }

  let result = '';
  if (min !== null && min !== undefined && max !== null && max !== undefined && min !== max) {
    result = `${min}-${max}`;
  } else if (min !== null && min !== undefined) {
    result = `${min}`;
  } else if (max !== null && max !== undefined) {
    result = `${max}`;
  }

  if (unit) {
    result += ` ${unit}`;
  }
  if (condition) {
    result += ` ${condition}`;
  }
  return result;
}

// ========== Input Sub-Components ==========

interface SolidContentInputProps {
  value?: number;
  tolerance?: number;
  onValueChange: (val: string) => void;
  onToleranceChange: (val: string) => void;
  valuePlaceholder: string;
  tolerancePlaceholder: string;
}

const SolidContentInput: React.FC<SolidContentInputProps> = ({
  value,
  tolerance,
  onValueChange,
  onToleranceChange,
  valuePlaceholder,
  tolerancePlaceholder,
}) => (
  <div className="inspection-input-group">
    <input
      type="number"
      className="table-input-small"
      placeholder={valuePlaceholder}
      value={value ?? ''}
      onChange={e => onValueChange(e.target.value)}
      step="0.01"
    />
    <span className="input-label">%</span>
    <span className="input-separator">±</span>
    <input
      type="number"
      className="table-input-small"
      placeholder={tolerancePlaceholder}
      value={tolerance ?? ''}
      onChange={e => onToleranceChange(e.target.value)}
      step="0.01"
    />
    <span className="input-label">%</span>
  </div>
);

interface SolidContentWithNoteInputProps extends SolidContentInputProps {
  note?: string;
  onNoteChange: (val: string) => void;
  notePlaceholder: string;
}

const SolidContentWithNoteInput: React.FC<SolidContentWithNoteInputProps> = ({
  value,
  tolerance,
  note,
  onValueChange,
  onToleranceChange,
  onNoteChange,
  valuePlaceholder,
  tolerancePlaceholder,
  notePlaceholder,
}) => (
  <div className="inspection-input-group">
    <input
      type="number"
      className="table-input-small"
      placeholder={valuePlaceholder}
      value={value ?? ''}
      onChange={e => onValueChange(e.target.value)}
      step="0.01"
    />
    <span className="input-label">%</span>
    <span className="input-separator">±</span>
    <input
      type="number"
      className="table-input-small"
      placeholder={tolerancePlaceholder}
      value={tolerance ?? ''}
      onChange={e => onToleranceChange(e.target.value)}
      step="0.01"
    />
    <span className="input-label">%</span>
    <input
      type="text"
      className="table-input-medium"
      placeholder={notePlaceholder}
      value={note ?? ''}
      onChange={e => onNoteChange(e.target.value)}
    />
  </div>
);

interface ViscosityInputProps {
  value?: number;
  tolerance?: number;
  condition?: string;
  onValueChange: (val: string) => void;
  onToleranceChange: (val: string) => void;
  onConditionChange: (val: string) => void;
  valuePlaceholder: string;
  tolerancePlaceholder: string;
  conditionPlaceholder: string;
}

const ViscosityInput: React.FC<ViscosityInputProps> = ({
  value,
  tolerance,
  condition,
  onValueChange,
  onToleranceChange,
  onConditionChange,
  valuePlaceholder,
  tolerancePlaceholder,
  conditionPlaceholder,
}) => (
  <div className="inspection-input-group-column">
    <div className="inspection-input-row">
      <input
        type="number"
        className="table-input-small"
        placeholder={valuePlaceholder}
        value={value ?? ''}
        onChange={e => onValueChange(e.target.value)}
        step="1"
      />
      <span className="input-separator">±</span>
      <input
        type="number"
        className="table-input-small"
        placeholder={tolerancePlaceholder}
        value={tolerance ?? ''}
        onChange={e => onToleranceChange(e.target.value)}
        step="1"
      />
      <span className="input-label">cps</span>
    </div>
    <input
      type="text"
      className="table-input"
      placeholder={conditionPlaceholder}
      value={condition ?? ''}
      onChange={e => onConditionChange(e.target.value)}
    />
  </div>
);

interface ViscosityRangeInputProps {
  min?: number;
  max?: number;
  condition?: string;
  onMinChange: (val: string) => void;
  onMaxChange: (val: string) => void;
  onConditionChange: (val: string) => void;
  minPlaceholder: string;
  maxPlaceholder: string;
  conditionPlaceholder: string;
}

const ViscosityRangeInput: React.FC<ViscosityRangeInputProps> = ({
  min,
  max,
  condition,
  onMinChange,
  onMaxChange,
  onConditionChange,
  minPlaceholder,
  maxPlaceholder,
  conditionPlaceholder,
}) => (
  <div className="inspection-input-group-column">
    <div className="inspection-input-row">
      <input
        type="number"
        className="table-input-small"
        placeholder={minPlaceholder}
        value={min ?? ''}
        onChange={e => onMinChange(e.target.value)}
        step="1"
      />
      <span className="input-separator">-</span>
      <input
        type="number"
        className="table-input-small"
        placeholder={maxPlaceholder}
        value={max ?? ''}
        onChange={e => onMaxChange(e.target.value)}
        step="1"
      />
      <span className="input-label">cps</span>
    </div>
    <input
      type="text"
      className="table-input"
      placeholder={conditionPlaceholder}
      value={condition ?? ''}
      onChange={e => onConditionChange(e.target.value)}
    />
  </div>
);

export default InspectionTable;
